import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { SiteHeader, SiteFooter, StateCard } from './SiteChrome';
import { IconList, IconClock, IconPlay, IconCheck, IconShield } from './Icons';
import { BRAND_NAME } from './brand';
import { storage, progressKey, doneKey, formatTime, formatDuration, pluralRu } from './utils';
import './VideoPlayer.css';

const API_URL = process.env.REACT_APP_API_URL || '';

function PlaylistPage() {
  const { playlistId } = useParams();
  const navigate = useNavigate();
  const isInIframe = window.self !== window.top;
  const [playlist, setPlaylist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API_URL}/api/public/playlists/${playlistId}`)
      .then((r) => setPlaylist(r.data))
      .catch((err) => setError(err.response?.data?.detail || 'Плейлист не найден'))
      .finally(() => setLoading(false));
  }, [playlistId]);

  useEffect(() => {
    document.title = playlist ? `${playlist.title} — ${BRAND_NAME}` : BRAND_NAME;
  }, [playlist]);

  const watchUrl = (id) => `/watch/${id}?playlist_id=${playlistId}${isInIframe ? '&embed=1' : ''}`;

  const videos = useMemo(() => (playlist && playlist.videos) || [], [playlist]);
  const totalDuration = videos.reduce((acc, v) => acc + (v.duration || 0), 0);
  const doneCount = videos.filter((v) => storage.get(doneKey(v.id))).length;
  // «Продолжить» — первое непросмотренное видео
  const continueVideo = videos.find((v) => !storage.get(doneKey(v.id)));
  const hasProgress = doneCount > 0 || videos.some((v) => storage.get(progressKey(v.id)));

  const header = !isInIframe && <SiteHeader />;

  if (loading) {
    return (
      <div className="watch-page">
        {header}
        <main className="watch-main">
          <div className="skeleton" style={{ maxWidth: 1120, height: 260, margin: '0 auto 24px', borderRadius: 28 }} />
          <div className="pl-page-list">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="skeleton" style={{ height: 78, borderRadius: 20 }} />
            ))}
          </div>
        </main>
      </div>
    );
  }

  if (error || !playlist) {
    return (
      <div className="watch-page">
        {header}
        <main className="watch-main watch-center">
          <StateCard tone="danger" title="Плейлист недоступен" text={typeof error === 'string' ? error : 'Плейлист не найден'} />
        </main>
      </div>
    );
  }

  return (
    <div className={`watch-page ${isInIframe ? 'is-embedded' : ''}`}>
      {header}
      <main className="watch-main pl-main">
        <section className="pl-page-hero">
          <span className="pl-page-eyebrow">
            <IconList size={15} /> Плейлист
          </span>
          <h1 className="pl-page-title">{playlist.title}</h1>
          {playlist.description && <p className="pl-page-desc">{playlist.description}</p>}

          <div className="pl-page-stats">
            <span className="pl-page-stat">
              <IconPlay size={14} /> {videos.length} {pluralRu(videos.length, 'видео', 'видео', 'видео')}
            </span>
            {totalDuration > 0 && (
              <span className="pl-page-stat">
                <IconClock size={16} /> {formatDuration(totalDuration)}
              </span>
            )}
            {doneCount > 0 && (
              <span className="pl-page-stat">
                <IconCheck size={16} /> Просмотрено {doneCount} из {videos.length}
              </span>
            )}
            {playlist.access_by_list && (
              <span className="pl-page-stat">
                <IconShield size={16} /> Доступ по списку
              </span>
            )}
          </div>

          {videos.length > 0 && (
            <div className="pl-page-actions">
              <button
                type="button"
                className="btn btn-white btn-lg"
                onClick={() => navigate(watchUrl((hasProgress && continueVideo ? continueVideo : videos[0]).id))}
              >
                <IconPlay size={16} />
                {hasProgress && continueVideo ? 'Продолжить просмотр' : 'Начать просмотр'}
              </button>
              {hasProgress && continueVideo && continueVideo.id !== videos[0].id && (
                <button type="button" className="btn btn-glass btn-lg" onClick={() => navigate(watchUrl(videos[0].id))}>
                  С первого видео
                </button>
              )}
            </div>
          )}
        </section>

        {videos.length > 0 ? (
          <ol className="pl-page-list">
            {videos.map((v, i) => {
              const done = !!storage.get(doneKey(v.id));
              const pos = storage.get(progressKey(v.id), 0);
              return (
                <li key={v.id}>
                  <button
                    type="button"
                    className={`pl-card ${done ? 'done' : ''}`}
                    style={{ animationDelay: `${Math.min(i, 12) * 40}ms` }}
                    onClick={() => navigate(watchUrl(v.id))}
                  >
                    <span className="pl-card-num">{done ? <IconCheck size={18} /> : i + 1}</span>
                    <span className="pl-card-body">
                      <span className="pl-card-title">{v.title}</span>
                      <span className="pl-card-meta">
                        {v.duration != null && (
                          <span>
                            <IconClock size={12} /> {formatTime(v.duration)}
                          </span>
                        )}
                        {done ? (
                          <span className="ok">Просмотрено</span>
                        ) : pos > 0 ? (
                          <span className="resume">Остановились на {formatTime(pos)}</span>
                        ) : null}
                      </span>
                    </span>
                    <span className="pl-card-play">
                      <IconPlay size={14} />
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
        ) : (
          <StateCard title="Пока пусто" text="В этот плейлист ещё не добавили видео." />
        )}
      </main>
      {!isInIframe && <SiteFooter />}
    </div>
  );
}

export default PlaylistPage;
